import "./settingsPanel.scss";

import _ from "lodash";
import PropTypes from "prop-types";
import React, { Fragment } from "react";
import ImmutablePropTypes from "react-immutable-proptypes";
import { connect } from "react-redux";

import FormCheckbox from "../../components/form/FormCheckbox";
import FormDropdown from "../../components/form/FormDropdown";
import InspectorField from "../../components/inspector/InspectorField";
import InspectorGroup from "../../components/inspector/InspectorGroup";
import InspectorRow from "../../components/inspector/InspectorRow";
import { TIMELINE_VIEW } from "../../constants/identifiers";
import itemCardDetailRows from "../../constants/itemCardDetailRows";
import skinOptions from "../../constants/skinOptions";
import TimeFormats from "../../constants/timeFormats";
import { TimelineLayoutDropdownOptions } from "../../constants/timelineLayoutOptions";
import dialogs from "../../dialogs";
import narrativeViewSelectors from "../../features/narrativeView/selectors";
import { appStateOperations, appStateSelectors } from "../../modules/appState";
import { batchActions } from "../../modules/chainReducer";
import { definitionOperations, definitionSelectors } from "../../modules/definition";
import { calendarOperations, calendarSelectors } from "../../modules/definition/calendar";
import { mindmapViewSettingsOperations } from "../../modules/definition/mindmapViewSettings";
import { narrativeViewSettingsOperations } from "../../modules/definition/narrativeViewSettings";
import {
  relationshipViewSettingsOperations,
  relationshipViewSettingsSelectors
} from "../../modules/definition/relationshipViewSettings";
import { subwayViewSettingsOperations, subwayViewSettingsSelectors } from "../../modules/definition/subwayViewSettings";
import { timelineViewSettingsOperations } from "../../modules/definition/timelineViewSettings";
import { typeSelectors } from "../../modules/definition/types";
import { itemSelectors } from "../../modules/items";
import { mindmapOperations } from "../../modules/mindmaps";
import { preferencesOperations, preferencesSelectors } from "../../modules/preferences";
import { timelineRangeOperations, timelineRangeSelectors } from "../../modules/timelineRange";
import { immutableRecords, PropTypeShapes } from "../../types";
import { mergeDispatchToProps } from "../../utils/codeSplit";
import { dateFormatOptions } from "../../utils/dateFormats";
import preprocessorQuery from "../../utils/preprocessorQuery";
import { getViewSettingsAndOperationsOfActiveViewType } from "../../utils/viewSettings";
import timelineViewSelectors from "../timelineView/selectors";
import { getShouldShowRelationshipOptions } from "./utils";

const isMobile = preprocessorQuery.isMobile();

const timeFormatOptions = () => {
  return TimeFormats.map(t => ({
    label: t.name,
    value: t.identifier
  }));
};

const layoutOptions = () => {
  return TimelineLayoutDropdownOptions.map(o => ({
    label: o.name,
    value: o.identifier,
    description: o.description
  }));
};

const timelineLayout = useProps => (
  <InspectorRow>
    <InspectorField label="Timeline Layout">
      <FormDropdown
        options={layoutOptions()}
        value={useProps.eventLayout}
        onChange={useProps.onEventLayoutChange}
        showDescription={true}
        title="Timeline Layout"
      />
    </InspectorField>
  </InspectorRow>
);

const getRelationshipTypesOptions = useProps => {
  const { activeViewType } = useProps;
  const isTimelineView = activeViewType === TIMELINE_VIEW;
  const disabled = isTimelineView && useProps.showMarkerOnly;
  const shouldShowRelationshipOptions = getShouldShowRelationshipOptions(activeViewType);

  if (shouldShowRelationshipOptions) {
    const { operations, viewSettings } = getViewSettingsAndOperationsOfActiveViewType(useProps);
    const { showRelationshipTypes } = viewSettings;

    let checkboxRows = useProps.referenceTypesOfView.map(t => (
      <InspectorField key={t.id}>
        <FormCheckbox
          label={t.label}
          checkboxState={showRelationshipTypes.includes(t.id)}
          onChange={_newState => useProps.onChangeCardDisplay(operations.toggleShowRelationshipTypes([t.id]))}
          disabled={disabled}
        />
      </InspectorField>
    ));

    return checkboxRows.size > 0 ? (
      <InspectorGroup label="Relationships">
        <InspectorRow>{checkboxRows}</InspectorRow>
      </InspectorGroup>
    ) : null;
  }
};

const lockScrolling = useProps => (
  <Fragment>
    {!isMobile && !useProps.disableSplitView ? (
      <InspectorRow>
        <InspectorField>
          <FormCheckbox
            label="Sync Scrolling"
            disabled={false}
            checkboxState={useProps.scrollingIsLocked}
            onChange={useProps.onScrollingIsLockedChange}
          />
        </InspectorField>
      </InspectorRow>
    ) : null}
  </Fragment>
);

class HarvardSettingsPanel extends React.PureComponent {
  constructor(props, context) {
    super(props, context);

    this.renderItemCardDetails = this.renderItemCardDetails.bind(this);
  }

  renderItemCardDetails = isMarkerOnly => {
    const imageRowIndex = 7;
    const narrativeRowIndex = 3;
    let rows = [...itemCardDetailRows];
    if (isMobile) {
      rows.splice(imageRowIndex, 1);
    }
    rows.splice(narrativeRowIndex, 1);

    const { viewSettings, operations } = getViewSettingsAndOperationsOfActiveViewType(this.props);

    const onCheckboxChange = (rowId, newState) => {
      // Which operation do we want to use, based on rowId (which specifies property name)
      const row = rows.find(r => r.id === rowId);
      const op = operations[row.onChangeFunctionName];
      this.props.onDispatchAction(op(newState));
    };

    const checkboxRows = _.map(rows, row => {
      const isChecked = viewSettings.get(row.id);
      return (
        <InspectorField key={row.id}>
          <FormCheckbox
            label={row.label}
            checkboxState={isChecked && !isMarkerOnly}
            onChange={newState => onCheckboxChange(row.id, newState)}
            disabled={_.isNil(isChecked) || isMarkerOnly}
          />
        </InspectorField>
      );
    });

    return <Fragment>{checkboxRows}</Fragment>;
  };

  onDeleteMindmap = () => {
    dialogs.okCancelPrompt("Are you sure you want to delete this mindmap?", result => {
      if (result) {
        this.props.onDeleteMindmap(this.props.activeViewMindmapId);
      }
    });
  };

  render() {
    const {
      calendar,
      onDateFormatChange,
      onTimeFormatChange,
      onIncludeDayNameChange,
      showMarkerOnly,
      onShowMarkerOnlyChange,
      activeViewType,
      dateOrder
    } = this.props;
    const isTimelineView = activeViewType === TIMELINE_VIEW;
    const isMarkerOnly = isTimelineView && showMarkerOnly;
    const showCardDetails = true;
    const showMarkerOnlyOption = !isMobile;

    return (
      <div className="settingsPanelWrapper">
        <div className="settingsPanel">
          <div className="settingsPanelHeader">Settings</div>
          <Fragment key="harvardDateSettings">
            <InspectorGroup label="Date Format" key="dateFormat">
              <InspectorRow>
                <InspectorField>
                  <Fragment>
                    <FormDropdown
                      options={dateFormatOptions(calendar.calendarType, dateOrder)}
                      value={calendar.dateFormat}
                      onChange={onDateFormatChange}
                      onToggleMenu={this.setEnabled}
                      title="Date Format"
                    />
                    <FormCheckbox
                      disabled={false}
                      label="Include day name"
                      checkboxState={calendar.dateFormatIncludesDayName}
                      onChange={onIncludeDayNameChange}
                    />
                  </Fragment>
                </InspectorField>
              </InspectorRow>
            </InspectorGroup>
            <InspectorGroup label="Time Format" key="timeFormat">
              <InspectorRow>
                <InspectorField>
                  <FormDropdown
                    options={timeFormatOptions()}
                    value={calendar.timeFormat}
                    onChange={onTimeFormatChange}
                    title="Time Format"
                  />
                </InspectorField>
              </InspectorRow>
            </InspectorGroup>
          </Fragment>
          {showCardDetails ? (
            <Fragment>
              <InspectorGroup label="Item Display" key="itemDisplay">
                <InspectorRow>
                  {showMarkerOnlyOption ? (
                    <InspectorField>
                      <FormCheckbox
                        key="markerOnly"
                        label={`Marker only`}
                        checkboxState={isMarkerOnly}
                        onChange={onShowMarkerOnlyChange}
                        disabled={false}
                      />
                    </InspectorField>
                  ) : null}
                  {this.renderItemCardDetails(isMarkerOnly)}
                </InspectorRow>
              </InspectorGroup>
              {getRelationshipTypesOptions(this.props)}
            </Fragment>
          ) : null}
          {!isMobile ? (
            <InspectorGroup label="Layout" key="harvardTimelineLayout">
              {timelineLayout(this.props)}
              {lockScrolling(this.props)}
            </InspectorGroup>
          ) : null}
          <InspectorGroup label="Theme" key="theme">
            <InspectorRow>
              <InspectorField>
                <FormDropdown
                  options={skinOptions}
                  value={this.props.skin}
                  onChange={this.props.onSkinChange}
                  title="Theme"
                />
              </InspectorField>
            </InspectorRow>
          </InspectorGroup>
        </div>
      </div>
    );
  }
}

HarvardSettingsPanel.propTypes = {
  calendar: PropTypeShapes.calendar.isRequired,
  dateOrder: PropTypes.string.isRequired,
  onDispatchAction: PropTypes.func.isRequired,
  onDateFormatChange: PropTypes.func.isRequired,
  onTimeFormatChange: PropTypes.func.isRequired,
  onIncludeDayNameChange: PropTypes.func.isRequired,
  timelineViewSettings: PropTypeShapes.timelineViewSettings.isRequired,
  narrativeViewSettings: PropTypeShapes.narrativeViewSettings.isRequired,
  relationshipViewSettings: PropTypeShapes.relationshipViewSettings.isRequired,
  onShowEntityColorChange: PropTypes.func.isRequired,
  mindmapViewSettings: PropTypeShapes.mindmapViewSettings.isRequired,
  applyViewSettingsToAllViews: PropTypes.func.isRequired,
  openViews: ImmutablePropTypes.map.isRequired,
  scrollingIsLocked: PropTypes.bool.isRequired,
  onScrollingIsLockedChange: PropTypes.func.isRequired,
  disableSplitView: PropTypes.bool.isRequired,
  showMarkerOnly: PropTypes.bool.isRequired,
  onShowMarkerOnlyChange: PropTypes.func.isRequired,
  activeViewType: PropTypes.string,
  eventLayout: PropTypes.string.isRequired,
  onEventLayoutChange: PropTypes.func.isRequired,
  mobileItemCardViewSettings: PropTypeShapes.itemCardViewSettings.isRequired,
  skin: PropTypes.string.isRequired,
  onSkinChange: PropTypes.func.isRequired,
  isNarrativeAllowed: PropTypes.bool.isRequired,
  showTodayLine: PropTypes.bool.isRequired,
  showDependencies: PropTypes.bool.isRequired,
  narrativeColumnLevel: PropTypes.number.isRequired,
  subwayDisplay: PropTypes.string.isRequired,
  onShowTodayLineChange: PropTypes.func.isRequired,
  onShowDependenciesChange: PropTypes.func.isRequired,
  onNarrativeColumnLevelChange: PropTypes.func.isRequired,
  onSubwayDisplayChange: PropTypes.func.isRequired,
  includeNarrative: PropTypes.bool.isRequired,
  typesById: ImmutablePropTypes.map.isRequired,
  onShowTypeChange: PropTypes.func.isRequired,
  activeViewZoom: PropTypes.shape({
    minValue: PropTypes.number.isRequired,
    maxValue: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired
  }).isRequired,
  subwayNumLines: PropTypes.string,
  onSubwayLineCountChange: PropTypes.func,
  onShowMindmapRelationshipLabels: PropTypes.func,
  activeViewMindmapId: PropTypes.string,
  onDeleteMindmap: PropTypes.func.isRequired,
  // Timeline range
  dateFrom: PropTypeShapes.date,
  dateTo: PropTypeShapes.date,
  onDateFromChange: PropTypes.func.isRequired,
  onDateToChange: PropTypes.func.isRequired,
  onDateFromAndToChange: PropTypes.func.isRequired,
  timelineContentRange: PropTypes.instanceOf(immutableRecords.TimelineRangeRecord),
  eventsOutOfRangeNumber: PropTypes.object.isRequired
};

const mapStateToProps = state => {
  const timelineViewSettings = definitionSelectors.getTimelineViewSettings(state.present);
  const activeViewId = appStateSelectors.getActiveViewId(state.present);
  const activeViewType = appStateSelectors.getActiveViewType(state.present);
  const activeViewMindmapId = appStateSelectors.getViewMindmapId(state.present, activeViewId);

  return {
    calendar: calendarSelectors.getCalendar(state.present),
    dateOrder: preferencesSelectors.getDateOrder(state.present),
    timelineViewSettings: timelineViewSettings,
    narrativeViewSettings: definitionSelectors.getNarrativeViewSettings(state.present),
    relationshipViewSettings: definitionSelectors.getRelationshipViewSettings(state.present),
    relationshipShowEntityColor: relationshipViewSettingsSelectors.getShowEntityColor(state.present),
    mindmapViewSettings: definitionSelectors.getMindmapViewSettings(state.present),
    openViews: appStateSelectors.getOpenViewsById(state.present),
    scrollingIsLocked: appStateSelectors.getScrollingIsLocked(state.present),
    disableSplitView: appStateSelectors.getDisableSplitView(state.present),
    showMarkerOnly: !timelineViewSettings.showItemCard,
    activeViewType,
    activeViewMindmapId,
    eventLayout: timelineViewSettings.eventLayout,
    mobileItemCardViewSettings: definitionSelectors.getMobileItemCardViewSettings(state.present),
    skin: preferencesSelectors.getSkin(state.present),
    isNarrativeAllowed: definitionSelectors.isNarrativeAllowed(state.present),
    showTodayLine: timelineViewSettings.showTodayLine,
    showDependencies: timelineViewSettings.showDependencies,
    narrativeColumnLevel: narrativeViewSelectors.getDepthForColumn(state.present),
    subwayDisplay: subwayViewSettingsSelectors.getSubwayViewSettingsDisplay(state.present),
    includeNarrative: definitionSelectors.isNarrativeAllowed(state.present),
    typesById: typeSelectors.getTypesById(state.present),
    activeViewZoom: appStateSelectors.getActiveViewZoom(state.present),
    zoomFrom: timelineViewSettings.zoomFrom,
    zoomTo: timelineViewSettings.zoomTo,
    dateFrom: timelineRangeSelectors.getRangeFrom(state.present),
    dateTo: timelineRangeSelectors.getRangeTo(state.present),
    timelineContentRange: itemSelectors.getDateRangeForTimelineContent(state.present),
    eventsOutOfRangeNumber: timelineViewSelectors.getEventsOutOfRangeNumber(state.present, activeViewId),
    subwayNumLines: subwayViewSettingsSelectors.getSubwayViewSettingsNumTextLines(state.present),
    referenceTypesOfView: typeSelectors.getReferenceTypesOfViewType(state.present, activeViewType)
  };
};

const readonlyActions = {
  onDispatchAction: a => a,
  onDateFormatChange: calendarOperations.changeDateFormat,
  onTimeFormatChange: calendarOperations.changeTimeFormat,
  onIncludeDayNameChange: calendarOperations.changeDateFormatIncludesDayName,
  onScrollingIsLockedChange: value => {
    if (value) {
      return appStateOperations.lockScrolling(value);
    } else {
      return appStateOperations.unlockScrolling(value);
    }
  },
  onShowMarkerOnlyChange: timelineViewSettingsOperations.changeShowMarkerOnly,
  onEventLayoutChange: timelineViewSettingsOperations.changeLayout,
  onSkinChange: preferencesOperations.setSkin,
  applyViewSettingsToAllViews: definitionOperations.applyViewSettingsToAllViews,
  onZoomFromChange: value => {
    return timelineViewSettingsOperations.changeZoomFrom(Number(value));
  },
  onZoomChange: appStateOperations.changeZoomSlider,
  onZoomToChange: value => {
    return timelineViewSettingsOperations.changeZoomTo(Number(value));
  },
  onShowDependenciesChange: timelineViewSettingsOperations.changeShowDependencies,
  onShowTodayLineChange: timelineViewSettingsOperations.changeShowTodayLine,
  onNarrativeColumnLevelChange: narrativeViewSettingsOperations.setColumnLevel,
  onSubwayDisplayChange: subwayViewSettingsOperations.changeDisplay,
  onSubwayLineCountChange: subwayViewSettingsOperations.changeTextLines,
  onChangeCardDisplay: op => {
    return op;
  },
  onShowTypeChange: (typeId, show) => timelineViewSettingsOperations.changeShowType(typeId, show),
  onShowMindmapRelationshipLabels: mindmapViewSettingsOperations.changeShowConnectorLabels,
  onShowEntityColorChange: relationshipViewSettingsOperations.changeShowEntityColor
};

const editingActions = {
  onDateFromChange: value => {
    return timelineRangeOperations.changeDateFrom(value);
  },
  onDateToChange: value => {
    return timelineRangeOperations.changeDateTo(value);
  },
  onDateFromAndToChange: (dateFrom, dateTo) => {
    return batchActions([
      timelineRangeOperations.changeDateFrom(dateFrom),
      timelineRangeOperations.changeDateTo(dateTo)
    ]);
  },
  onDeleteMindmap: mindmapOperations.deleteMindmap
};

const mapDispatchToProps = dispatch => mergeDispatchToProps(readonlyActions, editingActions, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(HarvardSettingsPanel);
